import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  Settings as SettingsIcon, 
  Database, 
  CheckCircle2,
  Cloud,
  Folder
} from 'lucide-react';
import { toast } from 'sonner';

export default function Settings() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  
  const [activeTab, setActiveTab] = useState('integrations');
  
  // Integration descriptions use translation keys
  const integrations = [
    {
      id: 'google-drive',
      name: 'Google Drive',
      icon: Cloud,
      descriptionKey: 'settingsPage.googleDriveDesc',
      connected: false,
      comingSoon: true,
    },
    {
      id: 'dropbox',
      name: 'Dropbox',
      icon: Folder,
      descriptionKey: 'settingsPage.dropboxDesc',
      connected: false,
      comingSoon: true,
    },
    {
      id: 's3',
      name: 'Amazon S3',
      icon: Database,
      descriptionKey: 'settingsPage.s3Desc',
      connected: false,
      comingSoon: true,
    },
    {
      id: 'onedrive',
      name: 'Microsoft OneDrive',
      icon: Cloud,
      descriptionKey: 'settingsPage.onedriveDesc',
      connected: false,
      comingSoon: true,
    },
  ];

  const handleConnect = (integrationId) => {
    toast.info(t('settingsPage.comingSoonToast'));
  };

  const tabs = [
    { id: 'integrations', labelKey: 'settingsPage.storageIntegrations', icon: Database },
    { id: 'preferences', labelKey: 'settingsPage.preferences', icon: SettingsIcon },
  ];

  return (
    <div 
      className="p-4 md:p-6 lg:p-8 space-y-6" 
      data-testid="settings-page"
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {/* Header */}
      <div className={isRTL ? 'text-right' : ''}>
        <div className={`flex items-center gap-3 mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <SettingsIcon className="w-8 h-8 text-primary" />
          <h1 className="font-heading text-4xl font-bold text-gray-900">
            {t('settingsPage.title')}
          </h1>
        </div>
        <p className="text-lg text-gray-600">
          {t('settingsPage.description')}
        </p>
      </div>

      {/* Tabs */}
      <div className={`flex gap-2 border-b border-gray-200 ${isRTL ? 'flex-row-reverse' : ''}`}>
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 font-medium transition-all ${isRTL ? 'flex-row-reverse' : ''} ${
                activeTab === tab.id
                  ? 'text-primary border-b-2 border-primary'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Icon className="w-4 h-4" />
              {t(tab.labelKey)}
            </button>
          );
        })}
      </div>

      {/* Content */}
      {activeTab === 'integrations' && (
        <div className="space-y-6">
          {/* Info Card */}
          <Card className="p-6 rounded-2xl bg-blue-50 border-blue-200">
            <div className={`flex items-start gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <Database className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className={isRTL ? 'text-right' : ''}>
                <h3 className="font-semibold text-blue-900 mb-2">
                  {t('settingsPage.externalStorageIntegration')}
                </h3>
                <p className="text-sm text-blue-800">
                  {t('settingsPage.externalStorageDesc')}
                </p>
              </div>
            </div>
          </Card>

          {/* Integration Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {integrations.map((integration) => {
              const Icon = integration.icon;
              return (
                <Card 
                  key={integration.id} 
                  className="p-6 rounded-2xl hover:shadow-md transition-all"
                >
                  <div className={`flex items-start justify-between mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center">
                        <Icon className="w-6 h-6 text-gray-600" />
                      </div>
                      <div className={isRTL ? 'text-right' : ''}>
                        <h3 className={`font-semibold text-gray-900 flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                          {integration.name}
                          {integration.comingSoon && (
                            <Badge className="bg-yellow-100 text-yellow-700 text-xs">
                              {t('settingsPage.comingSoon')}
                            </Badge>
                          )}
                        </h3>
                      </div>
                    </div>
                  </div>

                  <p className={`text-sm text-gray-600 mb-4 ${isRTL ? 'text-right' : ''}`}>
                    {t(integration.descriptionKey)}
                  </p>

                  <Button
                    className="w-full"
                    disabled={integration.comingSoon}
                    onClick={() => handleConnect(integration.id)}
                  >
                    {integration.comingSoon ? t('settingsPage.comingSoon') : t('settingsPage.connect')}
                  </Button>
                </Card>
              );
            })}
          </div>

          {/* Additional Info */}
          <Card className="p-6 rounded-2xl bg-gray-50">
            <h3 className={`font-semibold text-gray-900 mb-3 ${isRTL ? 'text-right' : ''}`}>
              {t('settingsPage.howItWorks')}
            </h3>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className={`flex items-start gap-2 ${isRTL ? 'flex-row-reverse text-right' : ''}`}>
                <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span>
                  <strong>{t('settingsPage.secureConnection')}:</strong> {t('settingsPage.secureConnectionDesc')}
                </span>
              </li>
              <li className={`flex items-start gap-2 ${isRTL ? 'flex-row-reverse text-right' : ''}`}>
                <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span>
                  <strong>{t('settingsPage.selectiveSync')}:</strong> {t('settingsPage.selectiveSyncDesc')}
                </span>
              </li>
              <li className={`flex items-start gap-2 ${isRTL ? 'flex-row-reverse text-right' : ''}`}>
                <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span>
                  <strong>{t('settingsPage.realtimeUpdates')}:</strong> {t('settingsPage.realtimeUpdatesDesc')}
                </span>
              </li>
              <li className={`flex items-start gap-2 ${isRTL ? 'flex-row-reverse text-right' : ''}`}>
                <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span>
                  <strong>{t('settingsPage.youStayInControl')}:</strong> {t('settingsPage.youStayInControlDesc')}
                </span>
              </li>
            </ul>
          </Card>
        </div>
      )}

      {activeTab === 'preferences' && (
        <Card className="p-12 rounded-2xl text-center">
          <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
            <SettingsIcon className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="font-heading text-xl font-semibold text-gray-900 mb-2">
            {t('settingsPage.moreSettingsComingSoon')}
          </h3>
          <p className="text-gray-600">
            {t('settingsPage.moreSettingsDesc')}
          </p>
        </Card>
      )}
    </div>
  );
}
